import React, {useMemo, useState} from "react";
import {GearItem, useDeleteGearItem, useGear, useGearCategories, useListToMap} from "../../api";
import {Collapse, Container, IconButton, List, ListItem, ListItemButton, ListItemText, Paper} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {ConfirmDialog} from "../common/ConfirmDialog";
import {EditGearItemModal} from "./EditGearItemModal";
import {AddGearItemModal} from "./AddGearItemModal";
import {SearchableList} from "../common/SearchableList";
import {GearCategoryMatches} from "./GearCategoriesList";

function GearItemMatches(item: GearItem, search: string) {
    return item.make.toLowerCase().includes(search)
        || item.model.toLowerCase().includes(search)
        || item.description.toLowerCase().includes(search);
}

export function GearList() {
    const gear_categories = useListToMap(useGearCategories()[0], (v) => v.id);
    const delete_gear_item = useDeleteGearItem();
    const [gear_items, update_gear_items] = useGear();
    const [gear_item_to_delete, set_gear_item_to_delete] = useState<GearItem | undefined>(undefined);
    const [gear_item_to_edit, set_gear_item_to_edit] = useState<GearItem | undefined>(undefined);
    const [is_deleting_gear_item, set_is_deleting_gear_item] = useState(false);

    const grouped_gear_items = useMemo(() => {
        let groups: Record<number, GearItem[]> = {};
        if (gear_items) {
            for (const item of gear_items) {
                if (!groups.hasOwnProperty(item.category_id)) {
                    groups[item.category_id] = [];
                }
                groups[item.category_id].push(item);
            }
        }
        return groups;
    }, [gear_items]);

    return <Container maxWidth="lg">
        <Paper elevation={4}>
            {gear_categories && gear_items &&
                <div>
                    <SearchableList list={Object.keys(gear_categories)}
                                    render={(category_id_str, i, search) => {
                                        const category_id = Number(category_id_str);
                                        const category = gear_categories[category_id];
                                        const group_gear_items = grouped_gear_items.hasOwnProperty(category_id) ? grouped_gear_items[category_id] : [];
                                        return <div key={`gear_list_${i}`}>
                                            <ListItemButton>
                                                <ListItemText primary={category.name}
                                                              secondary={category.description}/>
                                            </ListItemButton>
                                            <Collapse in={true}>
                                                <List>
                                                    {group_gear_items.map((item, j) => {
                                                        if (search.length === 0 || GearCategoryMatches(category, search) || GearItemMatches(item, search)) {
                                                            return <ListItem key={`gear_category_${i}_${j}`}
                                                                             secondaryAction={<IconButton
                                                                                 onClick={() => set_gear_item_to_delete(item)}>
                                                                                 <DeleteIcon/>
                                                                             </IconButton>}>
                                                                <ListItemButton onClick={() => set_gear_item_to_edit(item)}>
                                                                    <ListItemText primary={`${item.make} - ${item.model}`}
                                                                                  secondary={item.description}/>
                                                                </ListItemButton>
                                                            </ListItem>
                                                        }
                                                        return undefined;
                                                    })}
                                                </List>
                                            </Collapse>
                                        </div>
                                    }}
                                    element_matches={(category_id_str, search) => {
                                        const category_id = Number(category_id_str);
                                        const category = gear_categories[category_id];
                                        const group_gear_items = grouped_gear_items.hasOwnProperty(category_id) ? grouped_gear_items[category_id] : [];
                                        return GearCategoryMatches(category, search)
                                            || group_gear_items.some(value => GearItemMatches(value, search));
                                    }}/>
                    {gear_item_to_edit &&
                        <EditGearItemModal existing_categories={gear_categories}
                                           item_to_edit={gear_item_to_edit}
                                           onClose={() => set_gear_item_to_edit(undefined)}
                                           onSave={() => {
                                               set_gear_item_to_edit(undefined);
                                               update_gear_items();
                                           }}/>
                    }
                    <AddGearItemModal existing_categories={gear_categories}
                                      onChange={() => update_gear_items()}
                                      onClose={() => {}}/>
                </div>
            }
        </Paper>
        <ConfirmDialog is_open={gear_item_to_delete !== undefined}
                       title={`Delete ${gear_item_to_delete?.make} ${gear_item_to_delete?.model}?`}
                       content={`Are you sure you want to delete ${gear_item_to_delete?.make} ${gear_item_to_delete?.model}?`}
                       onClose={() => set_gear_item_to_delete(undefined)}
                       onConfirmed={() => {
                           if (gear_item_to_delete !== undefined) {
                               set_is_deleting_gear_item(true);
                               delete_gear_item(gear_item_to_delete.id, () => {
                                   set_gear_item_to_delete(undefined);
                                   update_gear_items();
                                   set_is_deleting_gear_item(false);
                               });
                           }
                       }}
                       is_busy={is_deleting_gear_item}/>
    </Container>
}